<template>
  <div class="container-fluid">
    <!-- page header -->
    <b-row class="mt-5 mb-3">
      <b-col sm="12" md="6">
        <div v-if="routerIsStudentsGroup">
          <h2>Liste des participants</h2>
          <b-btn
            :to="{
              name: 'admin.usersProfile',
              params: { id: users.data.data[0].group.teacher.id }
            }"
            variant="outline-info"
            pill
            >Retour au profil</b-btn
          >
        </div>

        <h2 v-else>Utilisateurs</h2>
      </b-col>
      <b-col sm="12" md="6" class="text-right" v-if="!routerIsStudentsGroup">
        <b-btn
          :to="{ name: 'admin.usersCreate' }"
          pill
          variant="info"
          class="text-white add-user"
        >
          <BIconPlus></BIconPlus>
          <span>Ajouter un utilisateur</span>
        </b-btn>
      </b-col>
    </b-row>

    <!-- type of users -->
    <b-form-group v-if="!routerIsStudentsGroup">
      <b-form-radio-group
        id="btn-radios-1"
        v-model="userTypeSelected"
        buttons
        button-variant="outline-primary"
        name="radios-btn-default"
        :options="userTypes"
      >
      </b-form-radio-group>
    </b-form-group>

    <!-- filter and search -->
    <div class="bg-light py-4 container-fluid">
      <b-form inline class="justify-content-between">
        <b-row align-v="center">
          <b-col sm="12" md="6">
            <b-input-group class="mb-2">
              <template v-slot:append>
                <b-btn
                  :disabled="batchActionDisabled || isActionloading"
                  variant="info"
                  class="text-white"
                  @click="onActions"
                  >Exécuter
                  <b-spinner small v-if="isActionloading"></b-spinner>
                </b-btn>
              </template>
              <b-form-select
                :disabled="batchActionDisabled || isActionloading"
                v-model="batchActionSelected"
                :options="batchActionOptions"
                id="select-batch"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null"
                    >Actions de masse</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-input-group>
          </b-col>
          <b-col sm="12" md="6">
            <b-btn
              class="mb-2"
              variant="link"
              @click="onChange({ status: 1 })"
              >{{ newUsersCount }}</b-btn
            >
          </b-col>
        </b-row>

        <div class="mb-2">
          <label class="mr-3 d-inline" for="inline-form-input-name"
            >Statut</label
          >
          <b-form-select
            v-model="userStatusSelected"
            :options="sortedStatusOption"
            class="mr-3"
            id="select-horizontal"
          >
            <template v-slot:first>
              <b-form-select-option :value="null">
                Choisir un statut
              </b-form-select-option>
            </template>
          </b-form-select>
          <label class="sr-only" for="inline-form-input-name">search</label>
          <b-input
            id="inline-form-input-name"
            class="mb-2 mr-sm-2 mb-sm-0"
            placeholder="Recherche"
            v-model="name"
            @change="onChange({ search: $event })"
          ></b-input>
          <b-btn :disabled="!name" pill variant="primary">Rechercher</b-btn>
        </div>
      </b-form>
    </div>
    <!-- table -->
    <b-table
      ref="selectableTable"
      selectable
      select-mode="multi"
      selected-variant="none"
      class="mb-5"
      show-empty
      stacked="md"
      :items="currentItem"
      outlined
      bordered
      :busy="isBusy"
      empty-text="Il n'y a pas d'usager correspondant à vos critères de recherche"
      :fields="currentFields"
      @row-selected="onRowSelected"
      no-select-on-click
    >
      <template v-slot:table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <p>Actualisation en cours...</p>
        </div>
      </template>
      <template v-slot:cell(check)="row">
        <template v-if="row.rowSelected">
          <b-btn variant="none" @click="unselectRow(row.index)">
            <b-icon icon="check-square"></b-icon>
          </b-btn>
        </template>
        <template v-else>
          <b-btn variant="none" @click="selectRow(row.index)">
            <b-icon icon="square"></b-icon>
          </b-btn>
        </template>
      </template>
      <template v-slot:cell(group)="row">
        <span v-html="row.value"></span>
      </template>
      <template v-slot:cell(detail)="row">
        <b-btn
          v-if="row.item.group && row.item.role == 'student'"
          :to="{
            name: getProfileRoute(row.item),
            params: { id: row.item.id, group: row.item.group.id }
          }"
          variant="none"
          ><BIconChevronRight
        /></b-btn>
        <b-btn v-else @click="userProfile(row.item)" variant="none"
          ><BIconChevronRight
        /></b-btn>
      </template>
      <!-- :current-page="table.currentPage" -->
    </b-table>

    <!-- User Interface controls -->
    <b-row>
      <b-col cols="4" class="my-1">
        <b-pagination
          pills
          v-model="table.currentPage"
          :total-rows="table.totalRows"
          :per-page="table.perPage"
          class="my-0"
          @change="pageChange"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/**
 * @file       Users list component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import store from "@/store";
import { mapState } from "vuex";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "adminUsers",

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      name: null,
      rowSelected: [],
      batchActionSelected: null,
      checkedItems: [],
      statusSelected: "new",
      userTypes: [
        { text: "Administrateurs", value: "admin" },
        {
          text: "Enseignants",
          value: "teacher"
        },
        { text: "Participants", value: "student" }
      ],
      statusOptions: [
        { text: "Inactif", value: 0 },
        { text: "Nouvelle demande", value: 1 },
        {
          text: "Invités",
          value: 2
        },
        { text: "Actifs", value: 3 },
        { text: "Archivés", value: 4 },
        { text: "Suspendu", value: 5 },
        { text: "Supprimé", value: 6 }
      ],
      statusOptionGroup: {
        2: { text: "supprimer", value: 2 },
        3: { text: "Suspendu", value: 3 },
        4: { text: "Archivé", value: 4 }
      },
      fields: {
        admin: [
          {
            key: "check",
            label: "Sélection",
            class: "text-center"
          },
          {
            key: "admin",
            label: "Nom de l'administrateur",
            sortable: true,
            sortDirection: "desc",
            formatter: (value, key, item) => {
              return `${item.name} ${item.last_name}`;
            }
          },
          {
            key: "status",
            label: "Statut",
            sortable: true,
            sortDirection: "desc",
            formatter: value => {
              const found = this.statusOptions.find(s => s.value == value);
              return found ? found.text : "";
            }
          },
          {
            key: "login_date",
            label: "Dernière connexion",
            sortable: true,
            sortDirection: "desc",
            formatter: value => this.$options.filters.formatDateTime(value)
          },
          {
            key: "detail",
            label: "Détail",
            class: "text-center"
          }
        ],
        teacher: [
          {
            key: "check",
            label: "Sélection",
            class: "text-center"
          },
          {
            key: "teacher",
            label: "Nom de l'enseignant",
            sortable: true,
            sortDirection: "desc",
            formatter: (value, key, item) => {
              return `${item.name} ${item.last_name}`;
            }
          },
          {
            key: "status",
            label: "Statut",
            sortable: true,
            sortDirection: "desc",
            formatter: value => {
              const found = this.statusOptions.find(s => s.value == value);
              return found ? found.text : "";
            }
          },
          {
            key: "login_date",
            label: "Dernière connexion",
            sortable: true,
            sortDirection: "desc",
            formatter: value => this.$options.filters.formatDateTime(value)
          },
          {
            key: "teacher.deliberations",
            label: "Nombre de délibération(s)",
            sortable: true,
            sortDirection: "desc",
            formatter: value => value.length
          },
          {
            key: "detail",
            label: "Détail",
            class: "text-center"
          }
        ],
        student: [
          {
            key: "check",
            label: "Sélection",
            class: "text-center"
          },
          {
            key: "student",
            label: "Nom du participant",
            sortable: true,
            sortDirection: "desc",
            formatter: (value, key, item) => {
              return `${item.name} ${item.last_name}`;
            }
          },
          {
            key: "group",
            label: "Groupe",
            sortable: true,
            formatter: (value, key, item) => {
              return item.group ? `${item.group.name}` : "---";
            }
          },
          {
            key: "teacher",
            label: "Enseignant",
            sortable: true,
            formatter: (value, key, item) => {
              return item.group
                ? `${item.group.teacher.user.name} ${item.group.teacher.user.last_name}`
                : "---";
            }
          },
          {
            key: "signalement",
            label: "Signalement",
            sortable: true,
            class: "text-center",
            formatter: (value, key, item) => {
              return item.signalement ? item.signalement : "---";
            }
          },
          {
            key: "suspension",
            label: "Suspendu",
            sortable: true,
            class: "text-center",
            formatter: (value, key, item) => {
              return item.group
                ? item.group.pivot.status == 3
                  ? "oui"
                  : "---"
                : item.status == 5
                ? "oui"
                : "---";
            }
          },
          {
            key: "archived",
            label: "Archivé",
            sortable: true,
            class: "text-center",
            formatter: (value, key, item) => {
              return item.status == 4 ? "oui" : "---";
            }
          },
          {
            key: "detail",
            label: "Détail",
            class: "text-center"
          }
        ]
      },
      profileRouteMap: {
        student: "admin.student.profile",
        teacher: "admin.usersProfile",
        admin: "admin.usersProfile"
      }
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    sortedStatusOption() {
      return [...this.statusOptions].sort((a, b) => (a.text > b.text ? 1 : -1));
    },
    ...mapState({
      users: state => state.admin.users,
      callFile: state => state.files.download.status,
      calluser: state => state.admin.user
    }),

    /**
     * Pager table.
     *
     * @return     {Object}
     */
    table() {
      return {
        totalRows: this.users.data.total,
        currentPage: this.users.data.current_page,
        perPage: this.users.data.per_page
      };
    },
    routerIsStudentsGroup() {
      return this.$route.name == "admin.users.per.group";
    },

    /**
     * Check if the status of users is busy.
     *
     * @return     {bool}  Busy or not.
     */
    isBusy() {
      return this.users.status === "fetching";
    },

    /**
     * Current user fields.
     * 
     * @return  {Object}    The fields
     */
    currentFields() {
      return this.routerIsStudentsGroup
        ? this.fields["student"]
        : this.fields[this.userTypeSelected];
    },

    /**
     * Current user.
     * 
     * @return  {Object}    The user
     */
    currentItem() {
      return this.routerIsStudentsGroup
        ? this.users.data.data
        : this.users.data.data;
    },

    /**
     * New users count message.
     * 
     * @return  {String}    The label
     */
    newUsersCount() {
      const count = this.users.data.data.filter(u => u.status == 1).length;
      let countStr = "nouvelles demandes d'accès";

      if (count == 1) {
        countStr = "Vous avez 1 nouvelle demande d'access";
      } else if (count > 1) {
        countStr = `Vous avez ${count} nouvelles demandes d'access`;
      }
      return countStr;
    },

    /**
     * User type selected.
     */
    userTypeSelected: {
      get() {
        return this.$store.state.admin.users.filters.role;
      },
      set(value) {
        this.$store.commit("admin/setUserFilters", {
          reset: true,
          fields: { role: value }
        });
        value == "student"
          ? this.$store.dispatch("admin/getMemberships")
          : this.$store.dispatch("admin/getUsers");
      }
    },

    /**
     * Batch action options.
     *
     * @return     {Object}  The options.
     */
    batchActionOptions() {
      let options = [
        {
          text: "Tout exporter les résultats en XLS",
          value: "exportAll",
          disabled: isNaN(this.userStatusSelected) && !this.name
        },
        {
          text: "Exporter la sélection en XLS",
          value: "exportSelecction",
          disabled: this.rowSelected.length <= 0
        },
        {
          text: "Supprimer tous les résultats",
          value: "deleteAll",
          disabled: isNaN(this.userStatusSelected) && !this.name,
          status: 6
        },
        {
          text: "Supprimer la sélection",
          value: "deleteSelecction",
          disabled: this.rowSelected.length <= 0,
          status: 6
        },

        {
          text: "Annuler la suppression tous les résultats",
          value: "undeleteAll",
          disabled: isNaN(this.userStatusSelected) && !this.name,
          status: 3
        },
        {
          text: "Annuler la suppression de la sélection",
          value: "undeleteSelecction",
          disabled: this.rowSelected.length <= 0,
          status: 3
        },

        {
          text: "Archivé tous les résultats",
          value: "archivedAll",
          disabled: isNaN(this.userStatusSelected) && !this.name,
          status: 4
        },
        {
          text: "Archivé la sélection",
          value: "archivedSelecction",
          disabled: this.rowSelected.length <= 0,
          status: 4
        },
        {
          text: "Désarchiver tous les résultats",
          value: "unarchiveAll",
          disabled: isNaN(this.userStatusSelected) && !this.name,
          status: 3
        },
        {
          text: "Désarchiver la sélection",
          value: "unarchiveSelecction",
          disabled: this.rowSelected.length <= 0,
          status: 3
        }
      ];
      return options;
    },

    /**
     * Check if batch action disabled.
     *
     * @return     {bool}  true or false.
     */
    batchActionDisabled() {
      let disabled = false;
      if (
        this.userStatusSelected == null &&
        this.name == null &&
        this.rowSelected.length == 0 &&
        !this.isActionloading
      ) {
        disabled = !disabled;
      }
      return disabled;
    },

    /**
     * User status selected.
     */
    userStatusSelected: {
      get() {
        return this.$store.state.admin.users.filters.status;
      },
      set(value) {
        this.$store.commit("admin/setUserFilters", {
          fields: { status: value }
        });
        this.userTypeSelected == "student"
          ? this.$store.dispatch("admin/getMemberships")
          : this.$store.dispatch("admin/getUsers");
      }
    },

    /**
     * Check if teacher selected.
     *
     * @return     {bool}  true or false.
     */
    isTeacherSelected() {
      return this.userTypeSelected !== "student";
    },

    /**
     * Check if action loading.
     *
     * @return     {bool}  true or false.
     */
    isActionloading() {
      return this.callFile === "fetching" || this.calluser.status == "fetching";
    }
  },

  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   (Unused)
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    if (to.name == "admin.users.per.group") {
      store.commit("admin/setUserFilters", {
        reset: true,
        fields: { group: parseInt(to.params.id), teacher: parseInt(2) }
      });
      store.dispatch("admin/getMemberships").then(() => next());
    } else {
      store.commit("admin/setUserFilters", {
        reset: true,
        fields: {
          role: "teacher"
        }
      });
      store.dispatch("admin/getUsers").then(() => next());
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Get profile route.
     *
     * @param      {Object}  item    The item.
     *
     * @return     {Object}  The route.
     */
    getProfileRoute(item) {
      return this.profileRouteMap[item.role];
    },

    /**
     * Select row.
     *
     * @param     {int}   index   Row index.
     */
    selectRow(index) {
      this.$refs.selectableTable.selectRow(index);
    },

    /**
     * Unselect row.
     *
     * @param     {int}   index   Row index.
     */
    unselectRow(index) {
      this.$refs.selectableTable.unselectRow(index);
    },

    /**
     * Row selected.
     *
     * @param     {int}   items   The items.
     */
    onRowSelected(items) {
      this.rowSelected = items;
    },

    /**
     * Change users page.
     *
     * @param      {int}  page     The page number
     */
    pageChange(page) {
      this.$store.commit("admin/setUserFilters", { fields: { page } });
      this.userTypeSelected == "student"
        ? this.$store.dispatch("admin/getMemberships")
        : this.$store.dispatch("admin/getUsers");
    },

    /**
     * Change user filters.
     *
     * @param      {Object}  select    The filters
     */
    onChange(select) {
      this.$store.commit("admin/setUserFilters", {
        fields: { ...select }
      });
      this.userTypeSelected == "student"
        ? this.$store.dispatch("admin/getMemberships")
        : this.$store.dispatch("admin/getUsers");
    },

    /**
     * Change user profile..
     *
     * @param      {Object}  item    The user
     */
    userProfile(item) {
      this.$store.commit("admin/setUser", item);
      this.$router.push({
        name: this.getProfileRoute(item),
        params: { id: item.id }
      });
    },

    /**
     * Actions.
     */
    onActions() {
      const users = this.rowSelected.map(({ id }) => id);
      const payload = {};
      switch (this.batchActionSelected) {
        case "deleteAll":
        case "undeleteAll":
        case "unarchiveAll":
        case "archivedAll":
          if (this.name) {
            payload.search = this.name;
          }
          if (this.userStatusSelected) {
            payload.status_filter = this.userStatusSelected;
          }
          payload.status = this.batchActionOptions.find(
            batch => batch.value == this.batchActionSelected
          ).status;

          if (this.userTypeSelected == "student") {
            payload.all = true;
            store
              .dispatch("admin/putMemberships", payload)
              .then(() => {
                this.$root.$bvToast.toast(
                  `Mise à jour effectuée avec succès.`,
                  {
                    title: "Succès"
                  }
                );
              })
              .catch(() => {
                this.$root.$bvToast.toast(`La mise à jour à échouée.`, {
                  title: "Erreur",
                  variant: "danger"
                });
              });
          } else {
            store
              .dispatch("admin/patchUser", payload)
              .then(() => {
                this.$root.$bvToast.toast(
                  `Mise à jour effectuée avec succès.`,
                  {
                    title: "Succès"
                  }
                );
              })
              .catch(() => {
                this.$root.$bvToast.toast(`La mise à jour à échouée.`, {
                  title: "Erreur",
                  variant: "danger"
                });
              });
          }
          break;
        case "deleteSelecction":
        case "undeleteSelecction":
        case "unarchiveSelecction":
        case "archivedSelecction":
          payload.status = this.batchActionOptions.find(
            batch => batch.value == this.batchActionSelected
          ).status;
          if (this.userTypeSelected == "student") {
            payload.to_change_status = users;
            store
              .dispatch("admin/putMemberships", payload)
              .then(() => {
                this.$root.$bvToast.toast(
                  `Mise à jour effectuée avec succès.`,
                  {
                    title: "Succès"
                  }
                );
              })
              .catch(() => {
                this.$root.$bvToast.toast(`La mise à jour à échouée.`, {
                  title: "Erreur",
                  variant: "danger"
                });
              });
          } else {
            payload.user_ids = users;
            store
              .dispatch("admin/patchUser", payload)
              .then(() => {
                this.$root.$bvToast.toast(
                  `Mise à jour effectuée avec succès.`,
                  {
                    title: "Succès"
                  }
                );
              })
              .catch(() => {
                this.$root.$bvToast.toast(`La mise à jour à échouée.`, {
                  title: "Erreur",
                  variant: "danger"
                });
              });
          }
          break;
        case "exportAll":
          payload.name = "Utilisateurs";
          if (this.name) {
            payload.search = this.name;
          }
          if (this.userStatusSelected) {
            payload.status = this.userStatusSelected;
          }
          if (this.userTypeSelected == "student") {
            this.$store.dispatch("files/getUserExportXls", payload);
          } else {
            store.dispatch("files/getFileDownload", {
              userType: this.userTypeSelected,
              ...payload
            });
          }
          break;
        case "exportSelecction":
          if (this.userTypeSelected == "student") {
            payload.name = "Utilisateurs";
            payload.users = this.rowSelected.map(selected => {
              return {
                id: selected.id,
                group: selected.group ? selected.group.pivot.group : 0
              };
            });
            this.$store.dispatch("files/getUserExportXls", payload);
          } else {
            store.dispatch("files/getFileDownload", {
              users,
              name: "Utilisateurs",
              userType: this.userTypeSelected
            });
          }

          break;
      }
    }
  }
};
</script>

<style lang="scss">
.btn-group-toggle {
  label.btn {
    border-radius: 50rem !important;
    margin-bottom: 0.5rem;
    margin-right: 0.5rem;
  }
}
</style>
